import { forwardRef } from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Right-rail slide-in. Pencil's side-panel chrome (frames `DSWj3`, `WjxKr`,
 * `HKpQ9`, `qiVHk`, `L7oUQ`) all share this exact structure: backdrop, white
 * card from the right, sticky header with title + close `×`, scrollable body,
 * sticky footer.
 *
 * Backed by `@radix-ui/react-dialog` (the same primitive shadcn's `Sheet`
 * uses). Radix gives us focus trap, restore-focus-on-close, scroll lock,
 * and Escape-to-close out of the box. The shadcn `Sheet`'s built-in close
 * button is omitted here because every caller renders its own X via
 * `<DrawerHeader>`.
 */
export function Drawer({
  open,
  onClose,
  width = "lg",
  children,
}: {
  open: boolean;
  onClose: () => void;
  /** `md` 480px · `lg` 560px · `xl` 720px (mirrors Pencil New Booking widths). */
  width?: "md" | "lg" | "xl";
  children: React.ReactNode;
}) {
  const widthClass =
    width === "md" ? "w-[480px]" : width === "xl" ? "w-[720px]" : "w-[560px]";

  return (
    <SheetPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetPrimitive.Portal>
        <SheetPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-foreground/30",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          )}
        />
        <SheetPrimitive.Content
          // Suppress Radix's "auto-focus the first focusable element on
          // open". Without this, refreshing the browser with a panel
          // already open programmatically focuses something inside,
          // which the browser's focus-visible heuristic then paints
          // with a ring — feels like an unexplained border. Keyboard
          // users still get the focus trap once they actively Tab in.
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={cn(
            "fixed inset-y-0 right-0 z-50 h-full bg-background border-l border-border shadow-xl flex flex-col",
            // Suppress the browser's default focus outline on the container
            // itself — Radix programmatically focuses Content as part of the
            // focus-trap, and our `--ring` (navy primary) makes that outline
            // read as a thick blue line on the panel's left edge (other
            // sides clip off-screen). Interactive children keep their own
            // focus-visible rings, so keyboard nav still has feedback.
            "outline-none focus:outline-none focus-visible:outline-none",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
            "data-[state=open]:duration-200 data-[state=closed]:duration-150",
            "max-w-full",
            widthClass,
          )}
        >
          {/* Required by Radix for a11y — visually hidden since each
              call site renders its own DrawerHeader with the heading. */}
          <SheetPrimitive.Title className="sr-only">Panel</SheetPrimitive.Title>
          <SheetPrimitive.Description className="sr-only">
            Side panel
          </SheetPrimitive.Description>
          {children}
        </SheetPrimitive.Content>
      </SheetPrimitive.Portal>
    </SheetPrimitive.Root>
  );
}

export function DrawerHeader({
  title,
  subtitle,
  status,
  onClose,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  status?: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="border-b border-border px-5 py-4 flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold tracking-tight truncate">{title}</h2>
          {status}
        </div>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{subtitle}</p>
        )}
      </div>
      <button
        type="button"
        onClick={onClose}
        className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-muted/60 shrink-0"
        aria-label="Close panel"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export const DrawerBody = forwardRef<
  HTMLDivElement,
  { children: React.ReactNode; className?: string }
>(({ children, className }, ref) => (
  <div ref={ref} className={cn("flex-1 overflow-y-auto", className)}>
    {children}
  </div>
));
DrawerBody.displayName = "DrawerBody";

export function DrawerFooter({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "border-t border-border px-5 py-3 flex items-center justify-end gap-2 bg-background",
        className,
      )}
    >
      {children}
    </div>
  );
}
